/**
 * Oscillator-based audio cues for state transitions.
 *
 * Provides ambient auditory feedback so the user knows what the app
 * is doing without looking at the screen. All cues use the Web Audio
 * API oscillator — no file dependencies.
 *
 * Design principles:
 * - Low volume — ambient feedback, not alerts
 * - Short duration — never longer than ~250 ms
 * - Distinct timbres per cue type for easy recognition
 * - Respects speaker mute & activation state (interaction-feedback cues bypass mute)
 *
 * Inspired by the original beta app's cueSent / cueReady oscillators.
 */

import { getAudioContext } from './audio';

// ── Types ──

export type CueName =
  | 'listeningStarted'
  | 'speechEndThinking'
  | 'firstToken'
  | 'responseComplete'
  | 'noReply'
  | 'error';

/** Configuration for an individual cue (allows runtime tuning) */
export interface CueConfig {
  enabled: boolean;
  volume: number; // 0–1 multiplier applied on top of the base volume
}

// ── Default cue configs ──

const defaultConfigs: Record<CueName, CueConfig> = {
  listeningStarted:  { enabled: true, volume: 1.0 },
  speechEndThinking: { enabled: true, volume: 1.0 },
  firstToken:        { enabled: true, volume: 1.0 },
  responseComplete:  { enabled: true, volume: 1.0 },
  noReply:           { enabled: true, volume: 1.0 },
  error:             { enabled: true, volume: 1.0 },
};

/** Live cue configs — clone defaults so mutations don't affect originals */
const cueConfigs: Record<CueName, CueConfig> = { ...defaultConfigs };

// ── Helpers ──

/**
 * Low-level oscillator tone.  Ramps gain to near-zero for a click-free
 * fade-out.  Returns immediately (fire-and-forget).
 */
function playTone(
  freq: number,
  durationSec: number,
  volume: number,
  type: OscillatorType = 'sine',
  delayMs = 0,
): void {
  try {
    const ctx = getAudioContext();
    const startTime = ctx.currentTime + delayMs / 1000;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.type = type;
    osc.frequency.setValueAtTime(freq, startTime);

    gain.gain.setValueAtTime(volume, startTime);
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + durationSec);

    osc.start(startTime);
    osc.stop(startTime + durationSec);
  } catch {
    // AudioContext might not be available yet — silently ignore
  }
}

/**
 * Rising sweep: frequency slides from `startFreq` to `endFreq` over
 * `durationSec`.  Used for the "listening started" cue.
 */
function playSweep(
  startFreq: number,
  endFreq: number,
  durationSec: number,
  volume: number,
  type: OscillatorType = 'sine',
): void {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.type = type;
    osc.frequency.setValueAtTime(startFreq, now);
    osc.frequency.linearRampToValueAtTime(endFreq, now + durationSec);

    gain.gain.setValueAtTime(volume, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + durationSec);

    osc.start(now);
    osc.stop(now + durationSec);
  } catch {
    // Silently ignore
  }
}

// ── Individual cue functions ──

/**
 * Listening started — short rising sweep (440→880 Hz, 120 ms).
 * Signals "I'm paying attention now".
 */
function cueListeningStarted(vol: number): void {
  playSweep(440, 880, 0.12, 0.18 * vol);
}

/**
 * Speech end → thinking — soft confirmation beep (880 Hz, 150 ms).
 * Signals "Got it, processing…".  Matches the old beta `cueSent`.
 */
function cueSpeechEndThinking(vol: number): void {
  playTone(880, 0.15, 0.20 * vol);
}

/**
 * First token received — subtle two-note ping (E5→G5).
 * Signals "Response is starting to arrive".
 */
function cueFirstToken(vol: number): void {
  playTone(659, 0.08, 0.12 * vol, 'sine', 0);
  playTone(784, 0.08, 0.12 * vol, 'sine', 80);
}

/**
 * Response complete / TTS finished — gentle falling two-tone (A5→E5).
 * Signals "All done, back to you".  Inspired by the beta `cueReady`.
 */
function cueResponseComplete(vol: number): void {
  playTone(660, 0.12, 0.15 * vol, 'sine', 0);
  playTone(880, 0.12, 0.15 * vol, 'sine', 120);
}

/**
 * No reply — soft double-tap acknowledgment (C5→E5, very short).
 * Signals "Got your message, nothing to say". Distinct from responseComplete.
 */
function cueNoReply(vol: number): void {
  playTone(523, 0.06, 0.10 * vol, 'sine', 0);    // C5
  playTone(659, 0.06, 0.10 * vol, 'sine', 80);    // E5
}

/**
 * Error — distinct low buzz (220 Hz, triangle wave, 250 ms).
 * Clearly different from all other cues.
 */
function cueError(vol: number): void {
  playTone(220, 0.25, 0.22 * vol, 'triangle', 0);
  playTone(196, 0.20, 0.18 * vol, 'triangle', 100);
}

// ── Cue dispatch map ──

const cueMap: Record<CueName, (vol: number) => void> = {
  listeningStarted:  cueListeningStarted,
  speechEndThinking: cueSpeechEndThinking,
  firstToken:        cueFirstToken,
  responseComplete:  cueResponseComplete,
  noReply:           cueNoReply,
  error:             cueError,
};

// ── Public API ──

/**
 * Play a named audio cue.
 *
 * @param name        Which cue to play
 * @param speakerMuted  Is the global speaker muted?
 * @param isActivated   Is the app in the active (non-idle) state?
 *
 * Cues are suppressed when the speaker is muted or the app is deactivated,
 * except for interaction-feedback cues (`speechEndThinking`, `responseComplete`)
 * and `error`, which always play when activated regardless of mute.
 */
export function playAudioCue(
  name: CueName,
  speakerMuted: boolean,
  isActivated: boolean,
): void {
  // Never play in idle/deactivated state
  if (!isActivated) return;

  // Respect speaker mute — but some cues are interaction feedback that should
  // always play so the user knows the app heard them / finished responding.
  const muteExempt: ReadonlySet<CueName> = new Set([
    'error',              // user should always hear errors
    'speechEndThinking',  // "I heard you, sending it now"
    'responseComplete',   // "I'm done responding"
    'noReply',            // "Got your message, nothing to say"
  ]);
  if (speakerMuted && !muteExempt.has(name)) return;

  const config = cueConfigs[name];
  if (!config.enabled) return;

  const fn = cueMap[name];
  fn(config.volume);
}

/**
 * Update the configuration for a specific cue at runtime.
 * Useful for a future settings UI.
 */
export function setCueConfig(name: CueName, patch: Partial<CueConfig>): void {
  Object.assign(cueConfigs[name], patch);
}

/**
 * Get a read-only copy of all cue configurations.
 */
export function getCueConfigs(): Record<CueName, CueConfig> {
  return { ...cueConfigs };
}
